/**
 * Smooth wheel and trackpad scrolling with Lenis (darkroom.engineering, MIT, assets/lenis.js).
 *
 * - Only on devices with a mouse or trackpad: touch keeps the native scrolling of the OS.
 * - Off with prefers-reduced-motion and in the theme editor (the layout does not load it there).
 * - The scroll position stays native, so sticky elements, the reveal observer, the header's
 *   scroll logic and anchor offsets (scroll-padding-top) keep working unchanged.
 * - Pauses while a sheet, the menu or the gallery zoom locks the page (html.om-locked).
 * - Leaves the wheel to elements that use it themselves: the 3D canvases of the configurators
 *   zoom with it (also inside their shadow DOM), dialogs and [data-lenis-prevent] scroll natively.
 */
const root = document.documentElement;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const hasFinePointer = window.matchMedia('(any-pointer: fine)').matches;

if (!reduceMotion && hasFinePointer) {
  const { default: Lenis } = await import('lenis');
  const wheelHosts = 'obermaier-badewannen, obermaier-maritim';
  const ownWheel = (node) =>
    node.matches('dialog, [data-lenis-prevent]') ||
    (node.localName === 'canvas' && !!(node.closest(wheelHosts) || node.getRootNode().host?.closest(wheelHosts)));

  const lenis = new Lenis({
    autoRaf: true,
    lerp: Number(root.dataset.smoothLerp) || 0.1,
    anchors: true,
    allowNestedScroll: true,
    stopInertiaOnNavigate: true,
    prevent: ownWheel,
  });

  // Hash links without a matching element (e.g. #eu-withdrawal=open of the withdrawal app) are no scroll
  // targets: keep them away from Lenis' anchor handling (it warns "Target not found"); the browser still
  // sets the hash, which opens the app's form.
  window.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href^="#"]');
    if (!link) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    if (id && !document.getElementById(id)) event.stopPropagation();
  }, true);

  const sync = () => (root.classList.contains('om-locked') ? lenis.stop() : lenis.start());
  new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['class'] });
  sync();

  window.Obermaier = Object.assign(window.Obermaier || {}, { lenis });
}
